/**
 * Offline write queue backed by IndexedDB.
 * Currently supports queuing ticket note additions (POST /api/tickets/{id}/events).
 * When the browser is offline, api.post() calls that we choose to shield can enqueue.
 * On the "online" event we replay the queue in order.
 */
import api from "@/lib/api";

const DB_NAME = "wineemotion-offline";
const STORE = "queue";
const VERSION = 1;

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") { reject(new Error("no-indexeddb")); return; }
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(mode) {
  const db = await openDb();
  return db.transaction(STORE, mode).objectStore(STORE);
}

export async function enqueueOfflineAction(action) {
  const store = await tx("readwrite");
  return new Promise((resolve, reject) => {
    const req = store.add({ ...action, created_at: new Date().toISOString() });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function listQueue() {
  const store = await tx("readonly");
  return new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

export async function queueCount() {
  try {
    const items = await listQueue();
    return items.length;
  } catch (_) { return 0; }
}

async function removeItem(id) {
  const store = await tx("readwrite");
  return new Promise((resolve) => {
    const req = store.delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
  });
}

/**
 * Attempt to add a note to a ticket. If offline, queue for later sync.
 * Returns { queued: boolean, event?: eventDoc }.
 */
export async function addTicketNote(ticketId, content) {
  const online = typeof navigator !== "undefined" ? navigator.onLine : true;
  if (online) {
    const { data } = await api.post(`/tickets/${ticketId}/notes`, { content });
    return { queued: false, event: data };
  }
  await enqueueOfflineAction({
    kind: "ticket-note",
    ticket_id: ticketId,
    payload: { content },
  });
  return { queued: true };
}

let syncing = false;
export async function syncQueue({ onProgress } = {}) {
  if (syncing) return { synced: 0, failed: 0 };
  syncing = true;
  const items = await listQueue();
  let synced = 0, failed = 0;
  for (const item of items) {
    try {
      if (item.kind === "ticket-note") {
        await api.post(`/tickets/${item.ticket_id}/notes`, item.payload);
      }
      await removeItem(item.id);
      synced += 1;
      onProgress?.({ synced, total: items.length });
    } catch (_) {
      failed += 1;
      // keep in queue for retry
    }
  }
  syncing = false;
  return { synced, failed };
}
